import PropTypes from 'prop-types';
import { useEffect, useReducer, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import Alert from '../../../components/ui/Alert';
import Button from '../../../components/ui/Button';
import Card, { CardContent, CardFooter, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Checkbox } from '../../../components/ui/Checkbox';
import Input from '../../../components/ui/Input';
import { Label } from '../../../components/ui/Label';
import { usePageTitle } from '../../../hooks/shared';
import { MAILER_PRICE, MAILER_TRIGGERS } from '../../../lib/mailer-triggers';
import { useAuthStore } from '../../identity/store/authStore';
import { useCreateMailerOrder, useMailerPreview } from '../hooks/useMailerOrders';

/**
 * Recipient card sub-component
 */
function RecipientCard({ recipient, index, onUpdate, onRemove, showRemove }) {
  const handleTriggerChange = (triggerKey, checked) => {
    const newTriggers = checked
      ? [...recipient.triggers, triggerKey]
      : recipient.triggers.filter((t) => t !== triggerKey);
    onUpdate(index, { ...recipient, triggers: newTriggers });
  };

  const handleFieldChange = (field, value) => {
    onUpdate(index, { ...recipient, [field]: value });
  };

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="text-base">
          {recipient.isSponsored ? 'Sponsor Mailers' : 'Your Mailers'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Recipient name and address */}
        <div>
          <Label htmlFor={`name-${index}`}>Recipient Name</Label>
          <Input
            id={`name-${index}`}
            placeholder="Full name"
            value={recipient.recipientName}
            onChange={(e) => handleFieldChange('recipientName', e.target.value)}
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor={`address-${index}`}>Mailing Address</Label>
          <Input
            id={`address-${index}`}
            placeholder="Street, City, State ZIP"
            value={recipient.mailingAddress}
            onChange={(e) => handleFieldChange('mailingAddress', e.target.value)}
            className="mt-1"
          />
        </div>

        {/* Trigger checkboxes */}
        <div>
          <Label className="text-sm font-semibold">Select mailer triggers:</Label>
          <div className="space-y-2 mt-3">
            {Object.entries(MAILER_TRIGGERS).map(([triggerKey, trigger]) => (
              <div key={triggerKey} className="flex items-center gap-2">
                <Checkbox
                  id={`trigger-${index}-${triggerKey}`}
                  checked={recipient.triggers.includes(triggerKey)}
                  onCheckedChange={(checked) => handleTriggerChange(triggerKey, checked)}
                />
                <Label
                  htmlFor={`trigger-${index}-${triggerKey}`}
                  className="cursor-pointer text-sm flex-1"
                >
                  {trigger.label} — ${MAILER_PRICE.toFixed(2)}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {/* Subtotal for this recipient */}
        <div className="pt-2 border-t border-neutral-200">
          <p className="text-sm">
            <span className="font-semibold">
              ${(recipient.triggers.length * MAILER_PRICE).toFixed(2)}
            </span>{' '}
            for this address
          </p>
        </div>
      </CardContent>

      {showRemove && (
        <CardFooter>
          <Button variant="destructive" size="sm" onClick={() => onRemove(index)}>
            Remove recipient
          </Button>
        </CardFooter>
      )}
    </Card>
  );
}

RecipientCard.propTypes = {
  recipient: PropTypes.shape({
    recipientName: PropTypes.string.isRequired,
    mailingAddress: PropTypes.string.isRequired,
    isSponsored: PropTypes.bool,
    triggers: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  showRemove: PropTypes.bool,
};

RecipientCard.defaultProps = {
  showRemove: true,
};

/**
 * Main setup page
 */
export default function MailerSetupPage() {
  usePageTitle('Set Up Mailers');

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [searchParams] = useSearchParams();
  const electionIdFromUrl = searchParams.get('electionId');

  const [electionId, setElectionId] = useState(electionIdFromUrl || '');
  const [recipients, setRecipients] = useState([
    {
      recipientName: '',
      mailingAddress: '',
      isSponsored: false,
      triggers: [],
    },
  ]);
  const [donationAmount, setDonationAmount] = useState(0);

  const { data: previewData } = useMailerPreview({
    recipients,
    donationAmount,
    electionId,
  });

  const { createOrder, isLoading: isCreating, error: createError } = useCreateMailerOrder();

  const recipientReducer = (state, action) => {
    switch (action.type) {
      case 'UPDATE':
        return state.map((r, i) => (i === action.index ? action.payload : r));
      case 'REMOVE':
        return state.filter((_, i) => i !== action.index);
      case 'ADD':
        return [
          ...state,
          {
            recipientName: '',
            mailingAddress: '',
            isSponsored: true,
            triggers: [],
          },
        ];
      default:
        return state;
    }
  };

  const [recipientState, dispatch] = useReducer(recipientReducer, recipients);

  useEffect(() => {
    setRecipients(recipientState);
  }, [recipientState]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!electionId) {
      alert('Please select an election');
      return;
    }

    const hasValidRecipients = recipients.every(
      (r) => r.recipientName && r.mailingAddress && r.triggers.length > 0
    );

    if (!hasValidRecipients) {
      alert(
        'Please complete all recipient information and select at least one trigger per recipient'
      );
      return;
    }

    createOrder({
      electionId,
      recipients,
      donationAmount,
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Alert variant="destructive">Please log in to set up mailers</Alert>
      </div>
    );
  }

  const subtotal = previewData?.subtotal || 0;
  const roundUpAmount = Math.ceil(subtotal) - subtotal;
  const suggestedTotal = subtotal + (donationAmount || roundUpAmount);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-2">Set Up Your Mailers</h1>
      <p className="text-neutral-600 mb-8">
        Choose when you&apos;d like to receive ballot guides during the election cycle
      </p>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main form */}
        <div className="lg:col-span-2 space-y-6">
          {createError && <Alert variant="destructive">Error: {createError.message}</Alert>}

          {/* Election selector */}
          <Card>
            <CardHeader>
              <CardTitle>Election</CardTitle>
            </CardHeader>
            <CardContent>
              <Label htmlFor="election">Select an election:</Label>
              <Input
                id="election"
                placeholder="Election ID or name"
                value={electionId}
                onChange={(e) => setElectionId(e.target.value)}
                className="mt-2"
              />
            </CardContent>
          </Card>

          {/* Your mailers */}
          <RecipientCard
            recipient={recipients[0]}
            index={0}
            onUpdate={(idx, rec) =>
              dispatch({
                type: 'UPDATE',
                index: idx,
                payload: rec,
              })
            }
            onRemove={() => {}}
            showRemove={false}
          />

          {/* Sponsored mailers */}
          {recipients.slice(1).map((recipient, idx) => {
            const actualIdx = idx + 1;
            return (
              <RecipientCard
                key={`recipient-${recipient.recipientName}-${actualIdx}`}
                recipient={recipient}
                index={actualIdx}
                onUpdate={(index, rec) =>
                  dispatch({
                    type: 'UPDATE',
                    index,
                    payload: rec,
                  })
                }
                onRemove={(index) =>
                  dispatch({
                    type: 'REMOVE',
                    index,
                  })
                }
                showRemove
              />
            );
          })}

          {/* Add sponsor button */}
          <Button type="button" variant="outline" onClick={() => dispatch({ type: 'ADD' })}>
            + Add a recipient
          </Button>
        </div>

        {/* Order summary sidebar */}
        <div>
          <Card className="sticky top-4">
            <CardHeader>
              <CardTitle>Order Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between text-sm">
                <span>Mailers:</span>
                <span>{previewData?.mailerCount || 0}</span>
              </div>

              <div className="flex justify-between text-sm">
                <span>Subtotal:</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>

              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <Label htmlFor="donation">Donation:</Label>
                  <Input
                    id="donation"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={donationAmount}
                    onChange={(e) => setDonationAmount(parseFloat(e.target.value) || 0)}
                    className="w-20 text-right text-sm"
                  />
                </div>
                <p className="text-xs text-neutral-500">
                  Round up: ${roundUpAmount.toFixed(2)} (optional)
                </p>
              </div>

              <div className="border-t border-neutral-200 pt-3 flex justify-between font-semibold">
                <span>Total:</span>
                <span>${suggestedTotal.toFixed(2)}</span>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col gap-2">
              <Button type="submit" disabled={isCreating} className="w-full">
                {isCreating ? 'Processing...' : 'Review & Pay'}
              </Button>
            </CardFooter>
          </Card>
        </div>
      </form>
    </div>
  );
}
